import { useEffect, useRef, useState } from 'react';
import { IconDownload, IconDeviceFloppy, IconPlayerPlay, IconX, IconAdjustments, IconStack2 } from '@tabler/icons-react';
import { usePrintEditorStore } from './state/usePrintEditorStore';
import useUnsavedExitGuard from '@/hooks/useUnsavedExitGuard';
import { T, chromeFont, EditorGlobalStyles } from './ui/figma';

/** Where the sidebar's open/shut choice is remembered. */
const LEFT_COLLAPSED_KEY = 'printEditor.leftCollapsed';
import LeftPanel from './panels/LeftPanel';
import RightPanel from './panels/RightPanel';
import BottomToolbar from './toolbar/BottomToolbar';
import CanvasViewport from './canvas/CanvasViewport';
import SplashScreen from './ui/SplashScreen';
import PanelResizer, { loadPanelWidth } from './ui/PanelResizer';
import { exportCanvasZip, selectionExportOptions } from './export/exportCanvas';
import { ensureDocumentFonts } from './fonts';

const AUTOSAVE_STORAGE_KEY = 'sohozkaj.printEditor.autosave';
const RIGHT_PANEL_KEY = 'sohozkaj.printEditor.rightPanelWidth';
const RIGHT_PANEL_MIN = 240;
const RIGHT_PANEL_MAX = 640;
/**
 * Default width. Wider than the 240 minimum because the panel now carries paired
 * inputs (W/H, X/Y), the frame list and the size-preset dropdown — at 240 those
 * are cramped and the preset labels truncate. Users who have already dragged the
 * panel keep their own width (it is persisted), and 240 is still draggable.
 */
const RIGHT_PANEL_DEFAULT = 288;
const AUTOSAVE_DEBOUNCE_MS = 1500;

/**
 * Figma-style print template editor shell.
 *
 * Layout: left panel (file / layers / elements) · full-bleed canvas with the
 * floating dark toolbar · right panel (design properties). Panels hide on
 * small screens and compact floating islands take over.
 */
export default function PrintTemplateEditor({
    initialDocument,
    mode = 'admin',
    onSave,
    onExport,
    onAutosave,
    saving = false,
    saveLabel,
    headerLeft = null,
    metaSlot = null,
    subtitle = null,
    thumbnailPreview = null,
    // The un-cropped picture behind the thumbnail, and the last crop made from it.
    thumbnailSource = null,
    thumbnailCrop = null,
    onThumbnailUpload,
    onThumbnailDelete,
    onThumbnailUseDesign,
    onThumbnailCrop,
    thumbnailCropping = false,
    // Unsaved work the STORE cannot see — the admin page's title, category and
    // status live in page state, and losing those to a refresh counts too.
    hostDirty = false,
    unsavedMessage,
}) {
    const shellRef = useRef(null);
    const saveRef = useRef(null);
    const savingRef = useRef(saving);
    const autosaveFnRef = useRef(onAutosave);
    savingRef.current = saving;
    autosaveFnRef.current = onAutosave;
    const { loadDocument, dirty } = usePrintEditorStore();

    /**
     * Has THIS editor's document been handed to the store yet?
     *
     * The store is a module singleton, so its `dirty` outlives the editor that
     * set it: open a template, edit it, leave, open another one, and the new
     * editor's first render still sees the old flag. That armed the exit guard on
     * a template nobody had touched — a confirm dialog on a page the user had
     * only just opened. The guard waits for this editor's own load instead.
     */
    const [loaded, setLoaded] = useState(false);

    // Warn before a refresh, a tab close, a link or the Back button throws the
    // work away. Off while a save runs, or the redirect a save does itself (new
    // template → /:id/edit) would ask the user about their own Save click.
    useUnsavedExitGuard(loaded && (dirty || hostDirty) && !saving, unsavedMessage);

    // Leaving the editor leaves nothing behind: the next one opens clean whatever
    // happened here, and no other page inherits an "unsaved" flag it can't clear.
    useEffect(() => () => usePrintEditorStore.getState().markClean(), []);

    // Brand splash while the template opens (see SplashScreen for the duration)
    const [splashDone, setSplashDone] = useState(false);
    // Which side panel is open as a drawer on a small screen (where both are hidden): 'left' | 'right' | null.
    const [mobilePanel, setMobilePanel] = useState(null);
    const [rightPanelW, setRightPanelW] = useState(
        () => loadPanelWidth(RIGHT_PANEL_KEY, RIGHT_PANEL_DEFAULT, RIGHT_PANEL_MIN, RIGHT_PANEL_MAX)
    );

    // Autosave preference (persisted); only meaningful when a host provides onAutosave.
    const [autosave, setAutosave] = useState(() => {
        try { return localStorage.getItem(AUTOSAVE_STORAGE_KEY) === '1'; } catch { return false; }
    });
    useEffect(() => {
        try { localStorage.setItem(AUTOSAVE_STORAGE_KEY, autosave ? '1' : '0'); } catch { /* ignore */ }
    }, [autosave]);

    // Autosave engine — debounced save after the document changes, when enabled.
    // Guards against loops (only fires while dirty) and overlap (skips while saving).
    useEffect(() => {
        if (!autosave || !autosaveFnRef.current) return undefined;
        let timer;
        const schedule = () => {
            clearTimeout(timer);
            timer = setTimeout(() => {
                if (savingRef.current) { schedule(); return; } // a save is in flight — retry shortly
                const s = usePrintEditorStore.getState();
                if (s.dirty && autosaveFnRef.current) {
                    autosaveFnRef.current(s.document);
                }
            }, AUTOSAVE_DEBOUNCE_MS);
        };
        const unsub = usePrintEditorStore.subscribe((state, prev) => {
            if (state.document !== prev.document) schedule(); // react only to edits
        });
        if (usePrintEditorStore.getState().dirty) schedule(); // catch edits made before toggling on
        return () => { clearTimeout(timer); unsub(); };
    }, [autosave]);

    // Load once per editor mount / when the template identity changes — not on every store tick
    useEffect(() => {
        setLoaded(false);
        loadDocument(initialDocument || null, { mode });
        // A saved template can name any of ~1900 Google families, none of which
        // the page declares up front. Pull them in as the document opens, or the
        // canvas lays every text box out against a fallback face first and only
        // reflows once the user happens to touch the font picker.
        ensureDocumentFonts(initialDocument?.elements || []);
        // Anything the store reports dirty from here on is this template's own.
        setLoaded(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally ignore loadDocument identity
    }, [initialDocument, mode]);

    // Editor-level shortcuts so they work even when layers/properties have focus
    useEffect(() => {
        const onKey = (e) => {
            // AltGr reports Ctrl+Alt on European Windows layouts — see CanvasViewport.
            if (e.ctrlKey && e.altKey && !e.metaKey) return;
            const mod = e.metaKey || e.ctrlKey;
            if (!mod) return;
            const key = e.key.toLowerCase();

            // Cmd/Ctrl+S → Save (works everywhere, even while a field is focused)
            if (key === 's') {
                e.preventDefault();
                saveRef.current?.();
                return;
            }

            // Undo/redo — skip while typing so the field keeps its own edit history
            const tag = document.activeElement?.tagName;
            if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
            if (document.activeElement?.isContentEditable) return;

            if (key === 'z' && !e.shiftKey) {
                e.preventDefault();
                usePrintEditorStore.getState().undo();
                return;
            }
            if (key === 'y' || (key === 'z' && e.shiftKey)) {
                e.preventDefault();
                usePrintEditorStore.getState().redo();
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);

    // Top-right download button — ALWAYS exports the whole template, never the selection.
    const handleExportFull = async () => {
        const doc = usePrintEditorStore.getState().document;
        if (onExport) {
            await onExport(doc);
            return;
        }
        await exportCanvasZip({ download: true, filenameBase: 'print-template', scale: 2, document: doc });
    };

    // Panel "Export" button — when elements are selected, exports only those (cropped,
    // transparent background); otherwise exports the whole artboard.
    const handleExportSelection = async () => {
        const doc = usePrintEditorStore.getState().document;
        if (onExport) {
            await onExport(doc);
            return;
        }
        const selection = selectionExportOptions();
        await exportCanvasZip({
            download: true,
            filenameBase: selection ? 'print-selection' : 'print-template',
            scale: 2,
            document: doc,
            ...(selection || {}),
        });
    };

    const handleSave = onSave
        ? () => onSave(usePrintEditorStore.getState().document)
        : null;
    // Keep the latest save handler reachable from the (mount-once) keyboard effect.
    saveRef.current = handleSave;

    const handleFit = () => {
        const vp = shellRef.current?.querySelector('[data-workspace="1"]');
        if (!vp) return;
        const rect = vp.getBoundingClientRect();
        usePrintEditorStore.getState().fitToView(rect.width, rect.height, { padding: 72 });
    };

    /* ---- Preview (presentation) mode ---------------------------------- *
     *
     * The play button hands the whole screen to the design: panels, toolbar and
     * every piece of selection chrome go, the canvas turns read-only, and all
     * that is left is zoom and pan (CanvasViewport's own zoom HUD). The hand tool
     * is switched on so a plain drag pans — in a view with nothing to select,
     * dragging can only sensibly mean "move the canvas".
     */
    const [presenting, setPresenting] = useState(false);
    const toolBeforePresent = useRef('select');

    /* ---- User's POV (admin only) -------------------------------------- *
     *
     * The admin builds the template against the full inspector, but the person
     * who fills it in gets a different editor: no layers, no artwork tools, and
     * only the elements the admin left editable. The right panel's "User's POV"
     * tab flips THIS editor into that mode over the same live document, so the
     * admin can check the fill-in experience without saving and reopening the
     * template on the user route. The document, its history and the viewport all
     * stay put — only the mode changes.
     */
    const [pov, setPov] = useState(false);
    const povActive = mode === 'admin' && pov;
    const effectiveMode = povActive ? 'user' : mode;
    useEffect(() => {
        usePrintEditorStore.getState().setMode(effectiveMode);
    }, [effectiveMode]);
    // A template that is opened, previewed, then swapped for another one must not
    // open the next one in the preview.
    useEffect(() => { setPov(false); }, [initialDocument]);

    // Fit once the layout has actually changed, or it measures the old width. A
    // plain timer, not requestAnimationFrame: rAF is suspended while the tab is in
    // the background, and entering the preview from a background tab would then
    // leave the canvas at whatever zoom and pan it had.
    const refitSoon = () => setTimeout(handleFit, 60);

    const enterPresent = () => {
        const store = usePrintEditorStore.getState();
        toolBeforePresent.current = store.tool;
        store.clearSelection();
        store.setTool('pan');
        setPresenting(true);
        // Browser fullscreen is best effort and deliberately NOT awaited: an iframe
        // or a locked-down host can refuse it (or never settle the promise), and the
        // chrome-free view must not hang on that. It refits again if it is granted.
        if (!document.fullscreenElement) {
            const req = (shellRef.current || document.documentElement).requestFullscreen?.();
            if (req?.then) req.then(refitSoon).catch(() => {});
        }
    };

    const exitPresent = () => {
        setPresenting(false);
        usePrintEditorStore.getState().setTool(toolBeforePresent.current || 'select');
        if (document.fullscreenElement) document.exitFullscreen?.()?.catch?.(() => {});
    };

    // The canvas is a different size the moment the panels and the toolbar come or
    // go, so refit whenever the mode flips — after React has laid the new shell out,
    // never on first mount (the viewport fits itself as the document opens).
    const presentedOnce = useRef(false);
    useEffect(() => {
        if (!presentedOnce.current) { presentedOnce.current = true; return undefined; }
        const t = refitSoon();
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- refit on the flip only
    }, [presenting]);

    // Esc leaves the preview. Browser fullscreen swallows the first Esc to drop out
    // of fullscreen, so `fullscreenchange` is what actually ends the mode there.
    useEffect(() => {
        if (!presenting) return undefined;
        const onKey = (e) => {
            if (e.key === 'Escape') { e.preventDefault(); exitPresent(); }
        };
        const onFsChange = () => { if (!document.fullscreenElement) exitPresent(); };
        window.addEventListener('keydown', onKey);
        document.addEventListener('fullscreenchange', onFsChange);
        return () => {
            window.removeEventListener('keydown', onKey);
            document.removeEventListener('fullscreenchange', onFsChange);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- exitPresent is stable enough here
    }, [presenting]);

    const toggleBrowserFullscreen = async () => {
        try {
            if (!document.fullscreenElement) {
                await (shellRef.current || document.documentElement).requestFullscreen?.();
            } else {
                await document.exitFullscreen?.();
            }
            // Refit after layout settles
            requestAnimationFrame(() => setTimeout(handleFit, 60));
        } catch {
            handleFit();
        }
    };

    // No left panel in the POV preview, so the floating file island takes over the
    // title and the way back — exactly as it does on the real user route.
    // Below sm a single unified top bar is shown instead; these floating title islands
    // cover the sm-and-up range (admin only up to md, where the left panel takes over).
    const floatingHeaderVisibility = effectiveMode === 'admin' ? 'hidden sm:flex md:hidden' : 'hidden sm:flex';
    // Only true when autosave is both switched on AND actually wired up by the host.
    const autosaveActive = !!onAutosave && autosave;

    // Remembered per browser: an admin who works on a 13" screen wants the sidebar
    // shut every time, not once. Read lazily and written on change; a browser that
    // refuses storage (private windows) just forgets, which is harmless here.
    const [leftCollapsed, setLeftCollapsed] = useState(() => {
        try {
            return window.localStorage.getItem(LEFT_COLLAPSED_KEY) === '1';
        } catch {
            return false;
        }
    });
    useEffect(() => {
        try {
            window.localStorage.setItem(LEFT_COLLAPSED_KEY, leftCollapsed ? '1' : '0');
        } catch {
            // no storage — the panel simply opens again next time
        }
    }, [leftCollapsed]);

    // Cmd/Ctrl+\ shows or hides the sidebar — Figma's "Show/Hide UI" key. Matched
    // by `code`, not `key`: with Shift held the key reads "|", and on many non-US
    // layouts the backslash glyph moves while the physical key does not. Allowed
    // while a field has focus — the chord means nothing to a text input — and
    // ignored whenever there is no sidebar to toggle (user mode, preview).
    useEffect(() => {
        if (effectiveMode !== 'admin' || presenting) return undefined;
        const onKey = (e) => {
            if (!(e.metaKey || e.ctrlKey) || e.altKey) return;
            if (e.code !== 'Backslash' && e.key !== '\\' && e.key !== '|') return;
            e.preventDefault();
            setLeftCollapsed((v) => !v);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [effectiveMode, presenting]);

    // The Design panel, shared by the desktop right rail and the mobile drawer.
    const rightPanelNode = (
        <RightPanel
            mode={effectiveMode}
            povPreview={povActive}
            onTogglePov={mode === 'admin' ? setPov : undefined}
            onSave={handleSave}
            onExport={handleExportSelection}
            onExportFull={handleExportFull}
            onPresent={enterPresent}
            autosave={autosave}
            onToggleAutosave={onAutosave ? setAutosave : undefined}
            saving={saving}
            saveLabel={saveLabel}
            onToggleFullscreen={toggleBrowserFullscreen}
            thumbnailPreview={thumbnailPreview}
            thumbnailSource={thumbnailSource}
            thumbnailCrop={thumbnailCrop}
            onThumbnailUpload={onThumbnailUpload}
            onThumbnailDelete={onThumbnailDelete}
            onThumbnailUseDesign={onThumbnailUseDesign}
            onThumbnailCrop={onThumbnailCrop}
            thumbnailCropping={thumbnailCropping}
        />
    );

    return (
        <div
            ref={shellRef}
            className={`flex h-dvh w-full overflow-hidden ${T.panel} ${T.text}`}
            style={chromeFont}
        >
            <EditorGlobalStyles />

            {!splashDone && <SplashScreen durationMs={3000} onDone={() => setSplashDone(true)} />}

            {/* Left panel — file / layers / elements (admin). Collapses to its icon
                rail: on a laptop the two sidebars leave little canvas between them,
                and the choice is remembered per browser because a small screen stays
                small. */}
            {effectiveMode === 'admin' && !presenting && (
                <aside
                    className={`${leftCollapsed ? 'w-12' : 'w-[288px]'} shrink-0 hidden md:flex
                        border-r ${T.border} ${T.panel} transition-[width] duration-150`}
                >
                    <LeftPanel
                        headerLeft={headerLeft}
                        metaSlot={metaSlot}
                        subtitle={subtitle}
                        autosaveActive={autosaveActive}
                        collapsed={leftCollapsed}
                        onToggleCollapsed={() => setLeftCollapsed((v) => !v)}
                    />
                </aside>
            )}

            {/* Canvas + floating chrome */}
            <div className="relative flex-1 min-w-0 flex flex-col min-h-0">
                <CanvasViewport readOnly={presenting} />
                {!presenting && <BottomToolbar />}

                {/* Leave the preview — the one control the mode keeps besides zoom.
                    Dimmed until the pointer is near it so it stays out of the design. */}
                {presenting && (
                    <button
                        type="button"
                        title="Leave preview (Esc)"
                        onClick={exitPresent}
                        className={`absolute top-4 right-4 z-40 h-9 px-3 flex items-center gap-1.5 rounded-[10px]
                            bg-[#1e1e1e]/85 text-white text-[11px] font-medium shadow-lg
                            opacity-40 hover:opacity-100 transition-opacity`}
                    >
                        <IconX size={14} />
                        Exit preview
                    </button>
                )}

                {/* Floating file island — when the left panel is unavailable (sm and up) */}
                {!presenting && (
                    <div className={`absolute top-3 left-3 z-30 items-center gap-1 max-w-[70%] ${floatingHeaderVisibility}`}>
                        <div className={`flex items-center gap-1 min-w-0 h-10 pl-1.5 pr-2.5 rounded-[13px] ${T.panel} shadow-[0_2px_10px_rgba(0,0,0,0.15),0_0_0_0.5px_rgba(0,0,0,0.06)]`}>
                            {headerLeft}
                            {dirty && !autosaveActive && (
                                <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#e68a00]" title="Unsaved changes" />
                            )}
                            {effectiveMode === 'admin' && (
                                <button
                                    type="button"
                                    title="Layers & template settings"
                                    onClick={() => setMobilePanel('left')}
                                    className={`md:hidden h-7 w-7 flex items-center justify-center rounded-[6px] ${T.hoverBg}`}
                                >
                                    <IconStack2 size={14} />
                                </button>
                            )}
                        </div>
                    </div>
                )}

                {/* Unified mobile top bar (below sm, where both side panels are hidden): one
                    full-width bar — title on the left (truncates), all actions on the right —
                    instead of two floating islands with an empty gap between them. */}
                {!presenting && (
                    <div className="sm:hidden absolute top-3 left-3 right-3 z-30 flex">
                        <div className={`flex min-w-0 flex-1 items-center gap-1 h-10 pl-1.5 pr-1 rounded-[13px] ${T.panel} shadow-[0_2px_10px_rgba(0,0,0,0.15),0_0_0_0.5px_rgba(0,0,0,0.06)]`}>
                            <div className="flex min-w-0 flex-1 items-center gap-1">
                                {headerLeft}
                                {dirty && !autosaveActive && (
                                    <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#e68a00]" title="Unsaved changes" />
                                )}
                            </div>
                            <div className="flex shrink-0 items-center gap-0.5 pl-1">
                                {effectiveMode === 'admin' && (
                                    <button type="button" title="Layers & template settings" onClick={() => setMobilePanel('left')}
                                        className={`h-7 w-7 flex items-center justify-center rounded-[6px] ${T.hoverBg}`}>
                                        <IconStack2 size={14} />
                                    </button>
                                )}
                                <button type="button" title="Design properties" onClick={() => setMobilePanel('right')}
                                    className={`h-7 w-7 flex items-center justify-center rounded-[6px] ${T.hoverBg}`}>
                                    <IconAdjustments size={14} />
                                </button>
                                <button type="button" title="Preview — full screen, canvas only" onClick={enterPresent}
                                    className={`h-7 w-7 flex items-center justify-center rounded-[6px] ${T.hoverBg}`}>
                                    <IconPlayerPlay size={14} />
                                </button>
                                {(mode !== 'user' || !onSave) && (
                                    <button type="button" title="Export template (PNG + PDF)" disabled={saving && !!onExport} onClick={handleExportFull}
                                        className={`h-7 w-7 flex items-center justify-center rounded-[6px] ${T.hoverBg} disabled:opacity-40`}>
                                        <IconDownload size={14} />
                                    </button>
                                )}
                                {handleSave && (
                                    <button type="button" disabled={saving} onClick={handleSave}
                                        className="h-7 px-2.5 flex items-center gap-1.5 rounded-[6px] bg-[#0d99ff] hover:bg-[#0b87e0] text-white text-[11px] font-semibold disabled:opacity-50">
                                        {mode === 'user' ? <IconDownload size={13} /> : <IconDeviceFloppy size={13} />}
                                        {saveLabel || (saving ? 'Saving…' : 'Save')}
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Right panel — design properties. Width is user-draggable from its
                left edge and remembered across sessions. */}
            <aside
                className={`relative shrink-0 border-l ${T.border} ${T.panel}
                    ${presenting ? 'hidden' : 'hidden sm:flex'}`}
                style={{ width: rightPanelW }}
            >
                <PanelResizer
                    width={rightPanelW}
                    onWidth={setRightPanelW}
                    min={RIGHT_PANEL_MIN}
                    max={RIGHT_PANEL_MAX}
                    // Double-click goes back to the DEFAULT width, not the minimum —
                    // "reset" should mean the width the panel ships at.
                    resetTo={RIGHT_PANEL_DEFAULT}
                    edge="left"
                    storageKey={RIGHT_PANEL_KEY}
                />
                {rightPanelNode}
            </aside>

            {/* Mobile drawers — on a small screen both side panels are hidden, so the
                design properties (and, for admin, the layers / template settings) are
                reached from the floating islands as a slide-in panel over the canvas. */}
            {!presenting && mobilePanel && (
                <div className="fixed inset-0 z-[60] flex">
                    <div className="absolute inset-0 bg-black/40" onClick={() => setMobilePanel(null)} />
                    <aside
                        className={`absolute top-0 bottom-0 ${mobilePanel === 'left' ? 'left-0 border-r' : 'right-0 border-l'} ${T.border} ${T.panel} flex w-[86vw] max-w-[320px] flex-col shadow-2xl`}
                    >
                        <div className={`flex h-9 shrink-0 items-center justify-between border-b ${T.border} pl-3 pr-1.5`}>
                            <span className="text-[11px] font-semibold">{mobilePanel === 'left' ? 'Layers' : 'Design'}</span>
                            <button
                                type="button"
                                onClick={() => setMobilePanel(null)}
                                className={`flex h-7 w-7 items-center justify-center rounded-[6px] ${T.hoverBg}`}
                                aria-label="Close panel"
                            >
                                <IconX size={15} />
                            </button>
                        </div>
                        <div className="flex min-h-0 flex-1">
                            {mobilePanel === 'left'
                                ? (
                                    <LeftPanel
                                        headerLeft={headerLeft}
                                        metaSlot={metaSlot}
                                        subtitle={subtitle}
                                        autosaveActive={autosaveActive}
                                        collapsed={false}
                                        onToggleCollapsed={() => setMobilePanel(null)}
                                    />
                                )
                                : rightPanelNode}
                        </div>
                    </aside>
                </div>
            )}
        </div>
    );
}
